import { IBooking, ITeeTime } from "redux/reducers/models/teetime";


const AFTERNOON_START_MIN = 12 * 60;
const EVENING_START_MIN = 17 * 60;

export type TMoveBookingDaypart = "morning" | "afternoon" | "evening";

export interface IMoveBookingDaypartSection {
  daypart: TMoveBookingDaypart;
  teeTimes: ITeeTime[];
}

export interface IMoveBookingTeeTimeData {
  fetchedTeeTimeCount: number;
  eligibleTeeTimes: ITeeTime[];
  eligibleTeeTimesOrdered: ITeeTime[];
  daypartSections: IMoveBookingDaypartSection[];
}

function startTimeToMinutesSinceMidnight(startTime: string | undefined): number | null {
  if (startTime == null || startTime === "") {
    return null;
  }
  const parts = String(startTime).split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return null;
  }
  return h * 60 + m;
}

function sortTeeTimesByStartTime(a: ITeeTime, b: ITeeTime): number {
  return (
    (startTimeToMinutesSinceMidnight(a.start_time) ?? 0) - (startTimeToMinutesSinceMidnight(b.start_time) ?? 0)
  );
}

export function buildMoveBookingTeeTimeData(
  moveBookingTeeTimes: ITeeTime[],
  selectedBooking: IBooking | null | undefined,
  holesAmount: number | null,
  playerAmount: number | null,
): IMoveBookingTeeTimeData {
  const fetchedTeeTimeCount = moveBookingTeeTimes?.length ?? 0;

  if (!moveBookingTeeTimes?.length || !selectedBooking?.tee_time) {
    return {
      fetchedTeeTimeCount,
      eligibleTeeTimes: [],
      eligibleTeeTimesOrdered: [],
      daypartSections: [],
    };
  }

  const currentTeeTimeId = selectedBooking.tee_time.id;
  const holes = holesAmount ?? selectedBooking.holes;
  const qty = playerAmount ?? selectedBooking.quantity;

  if (qty == null || holes == null) {
    return {
      fetchedTeeTimeCount,
      eligibleTeeTimes: [],
      eligibleTeeTimesOrdered: [],
      daypartSections: [],
    };
  }

  const eligibleTeeTimes = moveBookingTeeTimes.filter((teetime: ITeeTime) => {
    if (teetime.id === currentTeeTimeId) {
      return false;
    }
    if ((teetime.quantity_remaining ?? 0) < qty) {
      return false;
    }

    const divisions = teetime.divisions_available ?? [];
    if (holes === 18) {
      return teetime.blocked_type === "open" && divisions.includes(2) && teetime.turn_tee_time != null;
    }
    if (holes === 9) {
      return teetime.blocked_type === "open";
    }
    return false;
  });

  const morning: ITeeTime[] = [];
  const afternoon: ITeeTime[] = [];
  const evening: ITeeTime[] = [];

  for (const teetime of eligibleTeeTimes) {
    const mins = startTimeToMinutesSinceMidnight(teetime.start_time);
    const m = mins ?? 0;
    if (m < AFTERNOON_START_MIN) {
      morning.push(teetime);
    } else if (m < EVENING_START_MIN) {
      afternoon.push(teetime);
    } else {
      evening.push(teetime);
    }
  }

  morning.sort(sortTeeTimesByStartTime);
  afternoon.sort(sortTeeTimesByStartTime);
  evening.sort(sortTeeTimesByStartTime);

  const daypartSectionsRaw: IMoveBookingDaypartSection[] = [
    { daypart: "morning", teeTimes: morning },
    { daypart: "afternoon", teeTimes: afternoon },
    { daypart: "evening", teeTimes: evening },
  ];
  const daypartSections = daypartSectionsRaw.filter(section => section.teeTimes.length > 0);

  const eligibleTeeTimesOrdered = [...eligibleTeeTimes].sort(sortTeeTimesByStartTime);

  return { fetchedTeeTimeCount, eligibleTeeTimes, eligibleTeeTimesOrdered, daypartSections };
}
